import { Box, Button, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { useTranslatedText } from 'hooks/useTranslatedText';
import {
  type CartItem,
  isSeatedItem,
  type LocalCartItem,
} from 'restful/tickets';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import SeatingInfoGroup from 'components/@tickets/SeatingInfoGroup';
import TicketPrice from 'components/@tickets/TicketPrice';
import TicketQuantityIcon from 'components/@tickets/TicketQuantityIcon';

type Item = Pick<
  CartItem,
  | 'quantity'
  | 'name'
  | 'totalPrice'
  | 'subtotalPrice'
  | 'bookObjects'
  | 'bookObjectsVerbose'
> &
  Pick<LocalCartItem, 'bookObjectsInfo'>;

interface Props {
  item: Item;
  onDeleteItem?: (item: Item) => void;
}

const TicketInfoCard = ({ item, onDeleteItem }: Props) => {
  const localize = useTranslatedText();
  const seatingInfo = item.bookObjectsVerbose ?? item.bookObjectsInfo;

  return (
    <BorderStack sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          py: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
        }}
      >
        <TicketQuantityIcon quantity={item.quantity} />
        <Stack
          sx={{
            flexGrow: 1,
          }}
        >
          <Typography
            variant="small"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {localize(item.name)}
          </Typography>
          <TicketPrice
            discountedPrice={item.totalPrice}
            originalPrice={item.subtotalPrice}
            color="text.secondary"
          />
        </Stack>
        {!!onDeleteItem && (
          <div>
            {isSeatedItem(item?.bookObjectsInfo) ? (
              <Button
                variant="text"
                onClick={() => onDeleteItem(item)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id="event.seating.change_seats" />
              </Button>
            ) : (
              <Button
                sx={{ alignSelf: 'center' }}
                onClick={() => onDeleteItem(item)}
              >
                <Icon name="Trash" />
              </Button>
            )}
          </div>
        )}
      </Box>
      <SeatingInfoGroup bookObjectsInfo={seatingInfo} />
    </BorderStack>
  );
};

export default TicketInfoCard;
